import React from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Card } from "@/components/common";
import {
  Building2, Store, FileText, CalendarCheck, Wallet, Map, ArrowRight,
  Users2, Swords, Trophy, Clock,
} from "lucide-react";

const MODULES = [
  { to: "/app/facilities", label: "Find a Ground", desc: "Discover & book verified sports facilities", icon: Map, roles: ["*"] },
  { to: "/app/teams", label: "Teams", desc: "Create a team, invite players, find opponents", icon: Users2, roles: ["*"] },
  { to: "/app/matches", label: "Matches", desc: "Create matches, record scores, attach livestream", icon: Swords, roles: ["*"] },
  { to: "/app/enquiries", label: "Events & Trips", desc: "Submit event / sports-tourism enquiries", icon: FileText, roles: ["school_coordinator", "ops", "admin"] },
  { to: "/app/bookings", label: "Bookings", desc: "Track confirmed programs end-to-end", icon: CalendarCheck, roles: ["school_coordinator", "ops", "admin"] },
  { to: "/app/institutions", label: "Institutions", desc: "Register & manage your institutions", icon: Building2, roles: ["school_coordinator", "ops", "admin"] },
  { to: "/app/providers", label: "Facility Listings", desc: "List venues, coaches, transport & stays", icon: Store, roles: ["provider", "ops", "admin"] },
  { to: "/app/finance", label: "Finance", desc: "Payments & reconciliation", icon: Wallet, roles: ["finance", "admin"] },
];

const PLANNED = [
  { label: "Challenges", icon: Swords }, { label: "Tournaments", icon: Trophy },
  { label: "Leagues", icon: Trophy }, { label: "Match Day Live", icon: Users2 },
];

export default function Play() {
  const { hasRole } = useAuth();
  const mods = MODULES.filter((m) => m.roles.includes("*") || hasRole(...m.roles));

  return (
    <div className="animate-fadeUp">
      <PageHeader title="Play" subtitle="Everything to organise and run sports programs" />

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {mods.map((m) => {
          const Icon = m.icon;
          return (
            <Link key={m.to} to={m.to} data-testid={`play-${m.label.toLowerCase().split(" ")[0]}`}
              className="group rounded-2xl border border-border bg-card p-6 transition-all hover:-translate-y-1 hover:border-primary/50">
              <div className="flex items-center justify-between">
                <div className="inline-flex rounded-xl bg-primary/10 p-3 text-primary"><Icon className="h-6 w-6" /></div>
                <ArrowRight className="h-4 w-4 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
              </div>
              <h3 className="mt-4 font-display text-xl font-semibold">{m.label}</h3>
              <p className="mt-1 text-sm text-muted-foreground">{m.desc}</p>
            </Link>
          );
        })}
      </div>

      <Card className="mt-6">
        <div className="mb-3 flex items-center gap-2">
          <Clock className="h-5 w-5 text-muted-foreground" />
          <h3 className="font-display text-lg font-semibold">On the Phase 1 roadmap</h3>
        </div>
        <p className="mb-4 text-sm text-muted-foreground">Competitive play modules are being rolled out next. The event & booking workflow above is live today.</p>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {PLANNED.map((p) => {
            const Icon = p.icon;
            return (
              <div key={p.label} className="flex flex-col items-center gap-2 rounded-xl border border-dashed border-border bg-background/40 p-4 text-center">
                <Icon className="h-6 w-6 text-muted-foreground" />
                <span className="text-sm font-medium text-muted-foreground">{p.label}</span>
                <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-mono uppercase text-muted-foreground">Planned</span>
              </div>
            );
          })}
        </div>
      </Card>
    </div>
  );
}
